import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ArrowLeft, Search, Eye, Loader2, FileText, Calendar, Trash2, Download } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUT_PAIEMENT = {
    impayee: { label: 'Impayée', color: 'bg-red-50 text-red-700 border-red-200' },
    partiellement_payee: { label: 'Partiellement payée', color: 'bg-amber-50 text-amber-700 border-amber-200' },
    payee: { label: 'Payée', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    prestation_annulee: { label: 'Prestation annulée', color: 'bg-red-100 text-red-700 border-red-200' },
};

function formatDate(dateStr) {
    if (!dateStr) return '-';
    try { return format(new Date(dateStr), 'dd/MM/yyyy', { locale: fr }); } catch { return dateStr; }
}

const RECAP_FILTERS_KEY = 'facturation_recap_filters';
function readSavedFilters() {
    try { return JSON.parse(sessionStorage.getItem(RECAP_FILTERS_KEY)) || {}; } catch { return {}; }
}

export default function FacturationRecapPage() {
    const navigate = useNavigate();
    const [factures, setFactures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState(() => readSavedFilters().search || '');
    const [ebpFilter, setEbpFilter] = useState(() => readSavedFilters().ebpFilter || 'tous'); // tous | ebp_sup_zero
    const [dateFrom, setDateFrom] = useState(() => readSavedFilters().dateFrom || '');
    const [dateTo, setDateTo] = useState(() => readSavedFilters().dateTo || '');
    const [showDlDialog, setShowDlDialog] = useState(false);
    const [dlFrom, setDlFrom] = useState('');
    const [dlTo, setDlTo] = useState('');
    const [dlFiltre, setDlFiltre] = useState('ebp_sup_zero');
    const [showCreeDialog, setShowCreeDialog] = useState(false);
    const [creeRow, setCreeRow] = useState(null);
    const [creeMontant, setCreeMontant] = useState('');
    const [creeSaving, setCreeSaving] = useState(false);
    const [downloading, setDownloading] = useState(false);

    // Mémorise les filtres pour les retrouver au retour depuis une fiche facture
    useEffect(() => {
        sessionStorage.setItem(RECAP_FILTERS_KEY, JSON.stringify({ search, ebpFilter, dateFrom, dateTo }));
    }, [search, ebpFilter, dateFrom, dateTo]);

    useEffect(() => {
        setLoading(true);
        loadFactures();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadFactures = () => {
        axios.get(`${API}/api/factures/detail/list`, { withCredentials: true })
            .then(res => setFactures(res.data))
            .catch(() => setFactures([]))
            .finally(() => setLoading(false));
    };

    const handleTogglePro = async (e, f) => {
        e.stopPropagation();
        const next = !f.pro;
        if (!window.confirm(next
            ? `Marquer ${f.client_nom || 'cette facture'} comme PRO ? Le montant EBP deviendra le montant total de la facture (${formatEur(f.prix_ttc)}).`
            : `Retirer le statut PRO de ${f.client_nom || 'cette facture'} ? Le montant EBP redeviendra la somme des paiements (virements + chèques O).`)) {
            return;
        }
        try {
            await axios.patch(`${API}/api/factures/${f.id}/pro`, { pro: next }, { withCredentials: true });
            setFactures(prev => prev.map(x => x.id === f.id ? { ...x, pro: next, montant_ebp: next ? (Number(x.prix_ttc) || 0) : x.montant_ebp } : x));
            loadFactures();
        } catch {
            alert('Erreur lors de la mise à jour');
        }
    };

    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        const from = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
        const to = dateTo ? new Date(dateTo + 'T23:59:59') : null;
        return factures.filter(f => {
            if (s) {
                const match = (f.client_nom || '').toLowerCase().includes(s)
                    || (f.numero_facture || '').toLowerCase().includes(s);
                if (!match) return false;
            }
            if (ebpFilter === 'ebp_sup_zero' && !(f.montant_ebp > 0)) return false;
            if (ebpFilter === 'partiellement_payee' && !(f.statut_paiement === 'partiellement_payee' && !f.prestation_annulee)) return false;
            if (ebpFilter === 'impayee' && !(f.statut_paiement === 'impayee' && !f.prestation_annulee)) return false;
            if (ebpFilter === 'prestation_annulee' && !f.prestation_annulee) return false;
            if (ebpFilter === 'cree_oui' && f.statut_cree !== 'oui') return false;
            if (ebpFilter === 'cree_partiel' && f.statut_cree !== 'partiel') return false;
            if (ebpFilter === 'cree_non' && f.statut_cree !== 'non') return false;
            if (from || to) {
                if (!f.date_demenagement) return false;
                const d = new Date(f.date_demenagement);
                if (Number.isNaN(d.getTime())) return false;
                if (from && d < from) return false;
                if (to && d > to) return false;
            }
            return true;
        }).sort((a, b) => {
            // Tri par date de déménagement, de la plus récente à la plus ancienne
            const da = a.date_demenagement || '';
            const db = b.date_demenagement || '';
            if (da && db) return db.localeCompare(da);
            if (da) return -1;
            if (db) return 1;
            return 0;
        });
    }, [factures, search, ebpFilter, dateFrom, dateTo]);

    const totals = useMemo(() => ({
        prix_ttc: filtered.reduce((s, f) => s + (Number(f.prix_ttc) || 0), 0),
        ebp: filtered.reduce((s, f) => s + (Number(f.montant_ebp) || 0), 0),
        reste: filtered.reduce((s, f) => s + (Number(f.reste_a_creer) || 0), 0),
        solde_du: filtered.reduce((s, f) => s + (Number(f.solde_du) || 0), 0),
    }), [filtered]);

    const handleDelete = async (e, factureId) => {
        e.stopPropagation();
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette facture ?')) return;
        try {
            await axios.delete(`${API}/api/factures/${factureId}`, { withCredentials: true });
            setFactures(prev => prev.filter(f => f.id !== factureId));
        } catch {
            alert('Erreur lors de la suppression de la facture');
        }
    };

    const openCreeDialog = (e, f) => {
        e.stopPropagation();
        setCreeRow(f);
        setCreeMontant('');
        setShowCreeDialog(true);
    };

    const handleAddMontantCree = async () => {
        const val = parseFloat(creeMontant);
        if (!val || val <= 0) { alert('Veuillez saisir un montant supérieur à 0'); return; }
        setCreeSaving(true);
        try {
            await axios.patch(`${API}/api/factures/${creeRow.id}/montant-cree`, { ajout: val }, { withCredentials: true });
            setShowCreeDialog(false);
            loadFactures();
        } catch (err) {
            alert(err.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setCreeSaving(false);
        }
    };

    const handleResetMontantCree = async () => {
        if (!window.confirm(`Remettre à zéro le montant créé pour ${creeRow?.client_nom || 'cette facture'} ?`)) return;
        setCreeSaving(true);
        try {
            await axios.patch(`${API}/api/factures/${creeRow.id}/montant-cree`, { reset: true }, { withCredentials: true });
            setShowCreeDialog(false);
            loadFactures();
        } catch {
            alert('Erreur lors de la mise à jour');
        } finally {
            setCreeSaving(false);
        }
    };

    const handleDownloadEbpList = async () => {
        setDownloading(true);
        try {
            const params = { filtre: dlFiltre };
            if (dlFrom) params.date_debut = dlFrom;
            if (dlTo) params.date_fin = dlTo;
            const res = await axios.get(`${API}/api/factures/detail/ebp-liste-pdf`, {
                params, responseType: 'blob', withCredentials: true,
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `liste_${dlFiltre}_${dlFrom || 'debut'}_${dlTo || 'fin'}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            setShowDlDialog(false);
        } catch {
            alert('Erreur lors de la génération du PDF');
        } finally {
            setDownloading(false);
        }
    };

    const formatEur = (v) => (Number(v) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="facturation-recap-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/facturation')}
                        data-testid="back-to-facturation-btn"
                        className="text-slate-700 border-slate-300 hover:bg-slate-100"
                    >
                        <ArrowLeft size={16} className="mr-1" /> Retour
                    </Button>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Détail facturation
                        </h1>
                        <p className="mt-1 text-slate-500 text-sm">
                            Vue récapitulative des factures, EBP (virements + chèques O) et soldes.
                        </p>
                    </div>
                </div>
                <Button
                    className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white"
                    onClick={() => { setDlFrom(dateFrom); setDlTo(dateTo); setDlFiltre(ebpFilter); setShowDlDialog(true); }}
                    data-testid="download-ebp-list-btn"
                >
                    <Download size={16} className="mr-1.5" /> Télécharger ma liste
                </Button>
            </div>

            {/* Dialog téléchargement liste EBP */}
            <Dialog open={showDlDialog} onOpenChange={setShowDlDialog}>
                <DialogContent className="sm:max-w-md" data-testid="download-ebp-dialog">
                    <DialogHeader>
                        <DialogTitle>Télécharger ma liste</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-slate-500">
                        Choisissez le filtre et l'intervalle de dates. Le PDF contiendra : date, client, montant EBP, adresse de chargement, téléphone et déjà créée.
                    </p>
                    <div>
                        <label className="text-xs text-slate-500 block mb-1">Filtre</label>
                        <Select value={dlFiltre} onValueChange={setDlFiltre}>
                            <SelectTrigger data-testid="dl-filtre-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="tous">Tous</SelectItem>
                                <SelectItem value="ebp_sup_zero">EBP &gt; 0</SelectItem>
                                <SelectItem value="partiellement_payee">Partiellement payée</SelectItem>
                                <SelectItem value="impayee">Impayée</SelectItem>
                                <SelectItem value="prestation_annulee">Prestation annulée</SelectItem>
                                <SelectItem value="cree_oui">Créée : Oui</SelectItem>
                                <SelectItem value="cree_partiel">Créée : Partiel</SelectItem>
                                <SelectItem value="cree_non">Créée : Non</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex-1">
                            <label className="text-xs text-slate-500 block mb-1">Du</label>
                            <Input type="date" value={dlFrom} onChange={(e) => setDlFrom(e.target.value)} data-testid="dl-date-from" />
                        </div>
                        <div className="flex-1">
                            <label className="text-xs text-slate-500 block mb-1">Au</label>
                            <Input type="date" value={dlTo} onChange={(e) => setDlTo(e.target.value)} min={dlFrom || undefined} data-testid="dl-date-to" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDlDialog(false)}>Annuler</Button>
                        <Button
                            className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white"
                            onClick={handleDownloadEbpList}
                            disabled={downloading}
                            data-testid="dl-confirm-btn"
                        >
                            {downloading ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Download size={16} className="mr-1.5" />}
                            Télécharger le PDF
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog montant créé */}
            <Dialog open={showCreeDialog} onOpenChange={setShowCreeDialog}>
                <DialogContent className="sm:max-w-md" data-testid="montant-cree-dialog">
                    <DialogHeader>
                        <DialogTitle>Déjà créée — {creeRow?.client_nom || ''}</DialogTitle>
                    </DialogHeader>
                    <div className="text-sm text-slate-600 space-y-1">
                        <p>Montant EBP : <span className="font-semibold text-slate-900">{formatEur(creeRow?.montant_ebp)}</span></p>
                        <p>Déjà créé : <span className="font-semibold text-slate-900">{formatEur(creeRow?.montant_cree)}</span></p>
                        <p>Reste à créer : <span className="font-semibold text-slate-900">{formatEur(creeRow?.reste_a_creer)}</span></p>
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 block mb-1">Montant à ajouter (€)</label>
                        <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max={creeRow?.reste_a_creer}
                            value={creeMontant}
                            onChange={(e) => setCreeMontant(e.target.value)}
                            placeholder="0,00"
                            data-testid="montant-cree-input"
                        />
                    </div>
                    <DialogFooter className="gap-2">
                        {(creeRow?.montant_cree || 0) > 0 && (
                            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 sm:mr-auto" onClick={handleResetMontantCree} disabled={creeSaving} data-testid="montant-cree-reset-btn">
                                Remettre à zéro
                            </Button>
                        )}
                        <Button variant="outline" onClick={() => setShowCreeDialog(false)} disabled={creeSaving}>Annuler</Button>
                        <Button className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white" onClick={handleAddMontantCree} disabled={creeSaving} data-testid="montant-cree-save-btn">
                            {creeSaving ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : null}
                            Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Filtres */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
                <CardContent className="p-4 flex flex-col lg:flex-row gap-3 lg:items-center">
                    <div className="relative flex-1">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            placeholder="Rechercher par nom ou N° facture..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10"
                            data-testid="search-input"
                        />
                    </div>
                    <div className="flex flex-wrap gap-3 items-center">
                        <Select value={ebpFilter} onValueChange={setEbpFilter}>
                            <SelectTrigger className="w-[190px]" data-testid="ebp-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="tous">Tous</SelectItem>
                                <SelectItem value="ebp_sup_zero">EBP &gt; 0</SelectItem>
                                <SelectItem value="partiellement_payee">Partiellement payée</SelectItem>
                                <SelectItem value="impayee">Impayée</SelectItem>
                                <SelectItem value="prestation_annulee">Prestation annulée</SelectItem>
                                <SelectItem value="cree_oui">Créée : Oui</SelectItem>
                                <SelectItem value="cree_partiel">Créée : Partiel</SelectItem>
                                <SelectItem value="cree_non">Créée : Non</SelectItem>
                            </SelectContent>
                        </Select>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">Du</span>
                            <Input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                className="w-[150px] h-9"
                                data-testid="date-from"
                            />
                            <span className="text-xs text-slate-500">Au</span>
                            <Input
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                min={dateFrom || undefined}
                                className="w-[150px] h-9"
                                data-testid="date-to"
                            />
                        </div>
                        {(dateFrom || dateTo || ebpFilter !== 'tous' || search) && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-slate-500"
                                onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); setEbpFilter('tous'); }}
                                data-testid="reset-filters-btn"
                            >
                                Réinitialiser
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-10 flex justify-center">
                            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-10 text-center text-slate-500 text-sm">
                            Aucune facture trouvée avec ces filtres.
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-slate-50/70">
                                    <TableHead className="text-xs uppercase text-slate-500 font-semibold">Client</TableHead>
                                    <TableHead className="text-xs uppercase text-slate-500 font-semibold text-center">Pro</TableHead>
                                    <TableHead className="text-xs uppercase text-slate-500 font-semibold">Date déménagement</TableHead>
                                    <TableHead className="text-xs uppercase text-slate-500 font-semibold text-right">Prix TTC</TableHead>
                                    <TableHead className="text-xs uppercase text-slate-500 font-semibold text-right">Solde dû</TableHead>
                                    <TableHead className="text-xs uppercase text-slate-500 font-semibold">Paiement</TableHead>
                                    <TableHead className="text-xs uppercase text-slate-500 font-semibold text-right">Montant EBP</TableHead>
                                    <TableHead className="text-xs uppercase text-slate-500 font-semibold text-right">Reste EBP</TableHead>
                                    <TableHead className="text-xs uppercase text-slate-500 font-semibold text-center">Déjà créée</TableHead>
                                    <TableHead className="text-xs uppercase text-slate-500 font-semibold text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.map((f) => {
                                    const pInfo = f.prestation_annulee
                                        ? STATUT_PAIEMENT.prestation_annulee
                                        : (STATUT_PAIEMENT[f.statut_paiement] || STATUT_PAIEMENT.impayee);
                                    const isProg = !!f.is_programme;
                                    return (
                                        <TableRow
                                            key={f.id}
                                            className="cursor-pointer hover:bg-slate-50 transition-colors"
                                            onClick={() => navigate(`/facturation/${f.id}`, { state: { from: 'recap' } })}
                                            data-testid={`facture-row-${f.id}`}
                                        >
                                            <TableCell>
                                                <p className="font-medium text-slate-900 whitespace-normal break-words max-w-[240px]">{f.client_nom || '-'}</p>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={!!f.pro}
                                                    onClick={(e) => { e.preventDefault(); handleTogglePro(e, f); }}
                                                    readOnly
                                                    className="h-4 w-4 accent-[#2e7cd6] cursor-pointer"
                                                    title="Professionnel : le montant EBP devient le montant total de la facture (confirmation demandée)"
                                                    data-testid={`facture-pro-${f.id}`}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1.5">
                                                    <Calendar size={12} className="text-slate-400" />
                                                    <span className={`text-sm ${isProg ? 'text-slate-700' : 'text-slate-400 italic'}`}>
                                                        {isProg ? formatDate(f.date_demenagement) : 'Non programmé'}
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right text-slate-900 font-medium">{formatEur(f.prix_ttc)}</TableCell>
                                            <TableCell className={`text-right ${f.solde_du < 0 ? 'text-blue-600 font-medium' : 'text-slate-700'}`}>{formatEur(f.solde_du)}</TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={`text-xs ${pInfo.color}`}>{pInfo.label}</Badge>
                                            </TableCell>
                                            <TableCell className="text-right text-slate-700">{formatEur(f.montant_ebp)}</TableCell>
                                            <TableCell className={`text-right ${f.reste_a_creer > 0 ? 'text-orange-600 font-medium' : 'text-slate-400'}`} data-testid={`facture-reste-ebp-${f.id}`}>
                                                {formatEur(f.reste_a_creer)}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <button
                                                    type="button"
                                                    onClick={(e) => openCreeDialog(e, f)}
                                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-medium transition-colors whitespace-nowrap ${
                                                        f.statut_cree === 'oui'
                                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                                            : f.statut_cree === 'partiel'
                                                                ? 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100'
                                                                : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                                                    }`}
                                                    title="Cliquer pour ajouter un montant créé"
                                                    data-testid={`facture-deja-cree-${f.id}`}
                                                >
                                                    {f.statut_cree === 'oui'
                                                        ? 'Oui'
                                                        : f.statut_cree === 'partiel'
                                                            ? `Partiel — reste ${formatEur(f.reste_a_creer)}`
                                                            : 'Non'}
                                                </button>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={(e) => { e.stopPropagation(); navigate(`/facturation/${f.id}`, { state: { from: 'recap' } }); }}
                                                        data-testid={`facture-view-${f.id}`}
                                                    >
                                                        <Eye size={16} className="text-slate-600" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={(e) => handleDelete(e, f.id)}
                                                        data-testid={`facture-delete-${f.id}`}
                                                    >
                                                        <Trash2 size={16} className="text-red-500" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {filtered.length > 1 && (
                                    <TableRow className="bg-slate-50/50 font-semibold">
                                        <TableCell colSpan={3} className="text-slate-600 uppercase text-xs">Totaux</TableCell>
                                        <TableCell className="text-right text-slate-900">{formatEur(totals.prix_ttc)}</TableCell>
                                        <TableCell className={`text-right ${totals.solde_du < 0 ? 'text-blue-600' : 'text-slate-900'}`}>{formatEur(totals.solde_du)}</TableCell>
                                        <TableCell></TableCell>
                                        <TableCell className="text-right text-slate-900">{formatEur(totals.ebp)}</TableCell>
                                        <TableCell className={`text-right ${totals.reste > 0 ? 'text-orange-600' : 'text-slate-900'}`} data-testid="totaux-reste-ebp">{formatEur(totals.reste)}</TableCell>
                                        <TableCell colSpan={2}></TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <p className="text-xs text-slate-400 mt-4 flex items-center gap-1">
                <FileText size={12} />
                EBP = somme des paiements en virement et chèque O (les chèques SO et les espèces ne sont pas comptabilisés).
            </p>
        </div>
    );
}
